export interface SpeakingLocation {
  city: string;
  country: string;
  /** ISO 3166-1 alpha-2, used to highlight the country on the map. */
  countryCode: string;
  lat: number;
  lon: number;
  /** Years spoken there. Leave empty if you would rather not show years. */
  years: number[];
}

/**
 * Every city where I have spoken. Add an entry per city (not per talk) and list
 * the years in the `years` array. Coordinates are the city centre -- one decimal
 * of precision is plenty at world-map scale.
 */
export const speakingLocations: SpeakingLocation[] = [
  { city: 'Warsaw', country: 'Poland', countryCode: 'PL', lat: 52.23, lon: 21.01, years: [2019, 2022, 2023, 2024, 2025, 2026] },
  { city: 'Krakow', country: 'Poland', countryCode: 'PL', lat: 50.06, lon: 19.94, years: [2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026] },
  { city: 'Bydgoszcz', country: 'Poland', countryCode: 'PL', lat: 53.12, lon: 18.01, years: [2024] },
  { city: 'Gdansk', country: 'Poland', countryCode: 'PL', lat: 54.35, lon: 18.65, years: [2019, 2021, 2024] },
  { city: 'Katowice', country: 'Poland', countryCode: 'PL', lat: 50.26, lon: 19.02, years: [2020, 2025, 2026] },
  { city: 'Lodz', country: 'Poland', countryCode: 'PL', lat: 51.76, lon: 19.46, years: [2025] },
  { city: 'Berlin', country: 'Germany', countryCode: 'DE', lat: 52.52, lon: 13.4, years: [2026] },
  { city: 'London', country: 'United Kingdom', countryCode: 'GB', lat: 51.51, lon: -0.13, years: [2020, 2025, 2026] },
  { city: 'Helsinki', country: 'Finland', countryCode: 'FI', lat: 60.17, lon: 24.94, years: [2025, 2026] },
  { city: 'Sarajevo', country: 'Bosnia and Herzegovina', countryCode: 'BA', lat: 43.86, lon: 18.41, years: [2025] },
  { city: 'Bucharest', country: 'Romania', countryCode: 'RO', lat: 44.43, lon: 26.1, years: [2026] },
  { city: 'Tbilisi', country: 'Georgia', countryCode: 'GE', lat: 41.72, lon: 44.79, years: [2022, 2025] },
  { city: 'Stockholm', country: 'Sweden', countryCode: 'SE', lat: 59.33, lon: 18.07, years: [2025, 2026] },
  { city: 'Oslo', country: 'Norway', countryCode: 'NO', lat: 59.91, lon: 10.75, years: [2023] },
  { city: 'Kongsberg', country: 'Norway', countryCode: 'NO', lat: 59.67, lon: 9.65, years: [2023] },
  { city: 'Reykjavik', country: 'Iceland', countryCode: 'IS', lat: 64.15, lon: -21.94, years: [2026] },
  { city: 'Riga', country: 'Latvia', countryCode: 'LV', lat: 56.95, lon: 24.11, years: [2024] },
  { city: 'Istanbul', country: 'Turkey', countryCode: 'TR', lat: 41.01, lon: 28.98, years: [2025] },
  { city: 'Bratislava', country: 'Slovakia', countryCode: 'SK', lat: 48.15, lon: 17.11, years: [2024] },
  { city: 'Dnipro', country: 'Ukraine', countryCode: 'UA', lat: 48.46, lon: 35.05, years: [2019] },
  { city: 'Mexico City', country: 'Mexico', countryCode: 'MX', lat: 19.43, lon: -99.13, years: [2024] },
  { city: 'San Diego', country: 'United States', countryCode: 'US', lat: 32.72, lon: -117.16, years: [2024] },
  { city: 'Boston', country: 'United States', countryCode: 'US', lat: 42.36, lon: -71.06, years: [2024] },
  { city: 'Montreal', country: 'Canada', countryCode: 'CA', lat: 45.5, lon: -73.57, years: [2024] }
];

/** Cities grouped by country, both sorted alphabetically for the list view. */
export interface CountryGroup {
  country: string;
  countryCode: string;
  cities: SpeakingLocation[];
}

export function groupByCountry(locations: SpeakingLocation[]): CountryGroup[] {
  const groups = new Map<string, CountryGroup>();

  for (const location of locations) {
    let group = groups.get(location.countryCode);
    if (!group) {
      group = { country: location.country, countryCode: location.countryCode, cities: [] };
      groups.set(location.countryCode, group);
    }
    group.cities.push(location);
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      cities: [...group.cities].sort((a, b) => a.city.localeCompare(b.city))
    }))
    .sort((a, b) => a.country.localeCompare(b.country));
}

/** All years that appear in the data, newest first. */
export function allYears(locations: SpeakingLocation[]): number[] {
  const years = new Set<number>();
  for (const location of locations) {
    for (const year of location.years) years.add(year);
  }
  return Array.from(years).sort((a, b) => b - a);
}
